import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
} from "~/components/ui";
import { restApi } from "~/redux/restApi";
import { IAction } from "~/components/shared/add-action-modal/types";
import { format, startOfMonth, subDays, isSameMonth } from "date-fns";
import { DEFAULT_API_PAYLOAD } from "~/lib/constants";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  action: IAction | null;
  onSuccess?: () => void;
  viewOnly?: boolean;
}

function getCollectionDateRange(dateStr: string, cycle: string): string {
  if (!dateStr) return "Unknown";
  try {
    const targetDate = new Date(dateStr);
    if (isNaN(targetDate.getTime())) return dateStr;

    const monthStart = startOfMonth(targetDate);

    if (cycle === "Monthly") {
      return `${format(monthStart, "MMMM dd, yyyy")} – ${format(targetDate, "MMMM dd, yyyy")}`;
    }

    if (cycle === "Weekly") {
      let weekStart = subDays(targetDate, 6);
      if (!isSameMonth(weekStart, targetDate)) {
        weekStart = monthStart;
      }
      return `${format(weekStart, "MMMM dd, yyyy")} – ${format(targetDate, "MMMM dd, yyyy")}`;
    }

    if (cycle === "WorkWeek") {
      let weekStart = subDays(targetDate, 4);
      if (!isSameMonth(weekStart, targetDate)) {
        weekStart = monthStart;
      }
      return `${format(weekStart, "MMMM dd, yyyy")} – ${format(targetDate, "MMMM dd, yyyy")}`;
    }

    return format(targetDate, "MMMM dd, yyyy");
  } catch (e) {
    return dateStr;
  }
}

function getCollectionDescription(
  category: string,
  cycle: string,
  dateRange: string,
): string {
  const catLabel = category || "hours";
  if (cycle === "Monthly") {
    return `Monthly summary log for ${catLabel} from ${dateRange}.`;
  }
  if (cycle === "Weekly" || cycle === "WorkWeek") {
    return `Weekly aggregation log for ${catLabel} from ${dateRange}.`;
  }
  return `Daily log for ${catLabel} on ${dateRange}.`;
}

const FillWorkHoursModal = ({
  isOpen,
  onClose,
  action,
  onSuccess,
  viewOnly = false,
}: Props) => {
  const [fillWorkHoursTask, { isLoading }] =
    restApi.useFillWorkHoursTaskMutation();

  const { data: actionDetailResponse } = restApi.useGetSingleActionIdQuery(
    { ...DEFAULT_API_PAYLOAD, id: action?.id ?? "" },
    { skip: !action?.id },
  );
  const currentAction = actionDetailResponse?.result ?? action;

  const { data: commentData, isLoading: isLoadingComments } =
    restApi.useGetCommentByActionIdQuery(
      { ...DEFAULT_API_PAYLOAD, actionId: action?.id ?? "", pageSize: 50 },
      { skip: !action?.id },
    );
  const comments = (commentData as any)?.result?.data ?? [];

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      hours: "",
      comment: "",
      attachments: null as FileList | null,
    },
  });

  let meta: any = {};
  try {
    if (currentAction?.extraJson) {
      meta =
        typeof currentAction.extraJson === "string"
          ? JSON.parse(currentAction.extraJson)
          : currentAction.extraJson;
    }
  } catch (e) {
    //
  }

  useEffect(() => {
    if (isOpen) {
      if (viewOnly && meta.Hours !== undefined) {
        reset({
          hours: String(meta.Hours),
          comment: "",
          attachments: null,
        });
      } else {
        reset({
          hours: "",
          comment: "",
          attachments: null,
        });
      }
    }
  }, [isOpen, reset, viewOnly, meta.Hours]);

  if (!action) return null;

  const onSubmit = async (data: any) => {
    try {
      const attachments = data.attachments
        ? Array.from(data.attachments as FileList)
        : [];
      await fillWorkHoursTask({
        id: action.id,
        hours: Number(data.hours),
        comment: data.comment,
        attachments,
      }).unwrap();

      toast.success("Work hours submitted successfully.");
      onSuccess?.();
      onClose();
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to submit work hours");
    }
  };

  const collectionRangeStr = getCollectionDateRange(meta.Date, meta.Cycle);
  const displayTitle = viewOnly ? "View Work Hours" : "Submit Work Hours";

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-Medium text-primary-500">
            {displayTitle}
          </DialogTitle>
        </DialogHeader>

        <div className="mb-4 text-sm text-neutral-600 bg-neutral-50 p-3 rounded-lg border border-neutral-200 space-y-1.5">
          <div className="flex justify-between pb-1 border-b border-neutral-200">
            <span className="font-Medium">Category:</span>
            <span>{meta.Category || "Unknown"}</span>
          </div>
          <div className="flex justify-between pb-1 border-b border-neutral-200">
            <span className="font-Medium">Collection Date:</span>
            <span>{collectionRangeStr}</span>
          </div>
          <div className="text-xs text-neutral-400 italic pt-0.5">
            {getCollectionDescription(
              meta.Category,
              meta.Cycle,
              collectionRangeStr,
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="hours">
              Hours Worked <span className="text-destructive-500">*</span>
            </Label>
            <Input
              id="hours"
              type="number"
              step="0.01"
              placeholder="e.g. 8.5"
              disabled={viewOnly || isLoading}
              {...register("hours", {
                required: !viewOnly && "Hours are required",
                min: { value: 0, message: "Cannot be negative" },
              })}
            />
            {errors.hours && (
              <p className="text-xs text-destructive-500">
                {errors.hours.message as string}
              </p>
            )}
          </div>

          {!viewOnly && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="comment">Comment (Optional)</Label>
                <Textarea
                  id="comment"
                  placeholder="Add any additional notes..."
                  className="resize-none"
                  {...register("comment")}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="attachments">Attachments (Optional)</Label>
                <Input
                  id="attachments"
                  type="file"
                  multiple
                  {...register("attachments")}
                />
              </div>
            </>
          )}

          {viewOnly && comments.length > 0 && (
            <div className="space-y-3 pt-4 border-t">
              <p className="text-xs font-semibold uppercase tracking-wider text-neutral-400">
                Comments & Activity
              </p>
              {isLoadingComments ? (
                <div className="text-center py-2 text-xs text-neutral-400">
                  Loading comments...
                </div>
              ) : (
                <ul className="space-y-3 max-h-[200px] overflow-y-auto pr-1">
                  {comments.map((comment: any, idx: number) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-[10px] font-medium text-neutral-600">
                        {comment.createdBy?.name
                          ? comment.createdBy.name[0].toUpperCase()
                          : "?"}
                      </div>
                      <div className="flex-1 rounded-lg border border-neutral-100 bg-white px-3 py-2 shadow-sm text-xs">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-semibold text-neutral-700">
                            {comment.createdBy?.name}
                          </span>
                          <span className="text-[9px] text-neutral-400">
                            {comment.createdAt
                              ? new Date(comment.createdAt).toLocaleDateString()
                              : ""}
                          </span>
                        </div>
                        <p className="text-neutral-600">{comment.comment}</p>
                        {comment.attachments?.length > 0 && (
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            {comment.attachments.map((att: any, ai: number) => (
                              <a
                                key={ai}
                                href={att.url}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center gap-1 rounded border border-neutral-200 bg-neutral-50 px-1.5 py-0.5 text-[10px] text-blue-600 hover:underline"
                              >
                                {att.name || "Attachment"}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            {viewOnly ? (
              <Button type="button" variant="default" onClick={onClose}>
                Close
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button type="submit" variant="default" disabled={isLoading}>
                  Submit Hours
                </Button>
              </>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default FillWorkHoursModal;
